import {
  useState,
  createContext,
  FC,
  useContext,
  PropsWithChildren,
  useRef,
  useCallback,
} from 'react'

type SetVisibleChapterIdFn = (id: string) => void
type RegisterChapterHeadingFn = (chapterId: string) => void
type SetChapterHeadingVisibilityFn = (
  chapterId: string,
  isVisible: boolean,
) => void
type UnregisterChapterHeadingFn = (chapterId: string) => void

export type ArticleContextType = {
  visibleChapterId: string | null
  setVisibleChapterId: SetVisibleChapterIdFn
  registerChapterHeading: RegisterChapterHeadingFn
  setChapterHeadingVisibility: SetChapterHeadingVisibilityFn
  unregisterChapterHeading: UnregisterChapterHeadingFn
}

const VisibleChapterIdContext = createContext<string | null>(null)
const SetVisibleChapterIdContext = createContext<SetVisibleChapterIdFn>(
  () => null,
)
const RegisterChapterHeadingContext = createContext<RegisterChapterHeadingFn>(
  () => null,
)
const SetChapterHeadingVisibilityContext =
  createContext<SetChapterHeadingVisibilityFn>(() => null)
const UnregisterChapterHeadingContext =
  createContext<UnregisterChapterHeadingFn>(() => null)

export const ArticleProvider: FC<PropsWithChildren> = ({ children }) => {
  const [visibleChapterId, setVisibleChapterId] = useState<string | null>(null)
  const chapterOrderRef = useRef<Map<string, number>>(new Map())
  const registeredChapterIdsRef = useRef<Set<string>>(new Set())
  const visibleChapterIdsRef = useRef<Set<string>>(new Set())
  const lastScrollYRef = useRef<number>(0)

  const getVisibleChapterCandidatesInOrder = useCallback(() => {
    return Array.from(visibleChapterIdsRef.current)
      .map((chapterId) => ({
        chapterId,
        order:
          chapterOrderRef.current.get(chapterId) ?? Number.MAX_SAFE_INTEGER,
      }))
      .sort(
        (firstChapter, secondChapter) =>
          firstChapter.order - secondChapter.order,
      )
  }, [])

  const getPreviousRegisteredChapterId = useCallback(
    (chapterId: string): string | null => {
      const currentChapterOrder = chapterOrderRef.current.get(chapterId)
      if (currentChapterOrder === undefined) {
        return null
      }

      const previousCandidates = Array.from(registeredChapterIdsRef.current)
        .map((registeredChapterId) => ({
          chapterId: registeredChapterId,
          order:
            chapterOrderRef.current.get(registeredChapterId) ??
            Number.MAX_SAFE_INTEGER,
        }))
        .filter(({ order }) => order < currentChapterOrder)
        .sort(
          (firstChapter, secondChapter) =>
            secondChapter.order - firstChapter.order,
        )

      return previousCandidates[0]?.chapterId || null
    },
    [],
  )

  const syncVisibleChapterId = useCallback(() => {
    const currentScrollY =
      typeof window === 'undefined' ? 0 : Math.round(window.scrollY)
    const isScrollingUp = currentScrollY < lastScrollYRef.current
    lastScrollYRef.current = currentScrollY

    const visibleChapterCandidates = getVisibleChapterCandidatesInOrder()

    setVisibleChapterId((currentVisibleChapterId) => {
      const topVisibleChapterId = visibleChapterCandidates[0]?.chapterId
      if (topVisibleChapterId) {
        return topVisibleChapterId
      }

      if (isScrollingUp && currentVisibleChapterId) {
        return (
          getPreviousRegisteredChapterId(currentVisibleChapterId) ||
          currentVisibleChapterId
        )
      }

      return currentVisibleChapterId
    })
  }, [getPreviousRegisteredChapterId, getVisibleChapterCandidatesInOrder])

  const registerChapterHeading = useCallback<RegisterChapterHeadingFn>(
    (chapterId) => {
      if (!chapterOrderRef.current.has(chapterId)) {
        chapterOrderRef.current.set(chapterId, chapterOrderRef.current.size)
      }

      registeredChapterIdsRef.current.add(chapterId)
      syncVisibleChapterId()
    },
    [syncVisibleChapterId],
  )

  const setChapterHeadingVisibility =
    useCallback<SetChapterHeadingVisibilityFn>(
      (chapterId, isVisible) => {
        if (isVisible) {
          visibleChapterIdsRef.current.add(chapterId)
        } else {
          visibleChapterIdsRef.current.delete(chapterId)
        }

        syncVisibleChapterId()
      },
      [syncVisibleChapterId],
    )

  const unregisterChapterHeading = useCallback<UnregisterChapterHeadingFn>(
    (chapterId) => {
      registeredChapterIdsRef.current.delete(chapterId)
      visibleChapterIdsRef.current.delete(chapterId)

      if (registeredChapterIdsRef.current.size === 0) {
        chapterOrderRef.current.clear()
        visibleChapterIdsRef.current.clear()
        setVisibleChapterId(null)
        return
      }

      syncVisibleChapterId()
    },
    [syncVisibleChapterId],
  )

  return (
    <SetVisibleChapterIdContext.Provider value={setVisibleChapterId}>
      <RegisterChapterHeadingContext.Provider value={registerChapterHeading}>
        <SetChapterHeadingVisibilityContext.Provider
          value={setChapterHeadingVisibility}
        >
          <UnregisterChapterHeadingContext.Provider
            value={unregisterChapterHeading}
          >
            <VisibleChapterIdContext.Provider value={visibleChapterId}>
              {children}
            </VisibleChapterIdContext.Provider>
          </UnregisterChapterHeadingContext.Provider>
        </SetChapterHeadingVisibilityContext.Provider>
      </RegisterChapterHeadingContext.Provider>
    </SetVisibleChapterIdContext.Provider>
  )
}

export const useVisibleChapterId = (): string | null => {
  return useContext(VisibleChapterIdContext)
}

export const useSetVisibleChapterId = (): SetVisibleChapterIdFn => {
  return useContext(SetVisibleChapterIdContext)
}

export const useRegisterChapterHeading = (): RegisterChapterHeadingFn => {
  return useContext(RegisterChapterHeadingContext)
}

export const useSetChapterHeadingVisibility =
  (): SetChapterHeadingVisibilityFn => {
    return useContext(SetChapterHeadingVisibilityContext)
  }

export const useUnregisterChapterHeading = (): UnregisterChapterHeadingFn => {
  return useContext(UnregisterChapterHeadingContext)
}

export const useArticleContext = (): ArticleContextType => {
  return {
    visibleChapterId: useVisibleChapterId(),
    setVisibleChapterId: useSetVisibleChapterId(),
    registerChapterHeading: useRegisterChapterHeading(),
    setChapterHeadingVisibility: useSetChapterHeadingVisibility(),
    unregisterChapterHeading: useUnregisterChapterHeading(),
  }
}
